import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  type ElementRef,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  LucideChevronDown,
  LucideChevronUp,
  LucideDownload,
  LucideMinus,
  LucidePlus,
  LucidePrinter,
  LucideSearch,
  LucideX,
} from '@lucide/angular';
import { MmscanAlert } from '@mmscan/angular-ui/alert';
import { MmscanButton } from '@mmscan/angular-ui/button';
import { MmscanInput } from '@mmscan/angular-ui/forms';
import { MmscanIcon } from '@mmscan/angular-ui/icon';
import { MmscanSpinner } from '@mmscan/angular-ui/spinner';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import type { EventBus, PDFFindController, PDFLinkService, PDFViewer } from 'pdfjs-dist/web/pdf_viewer.mjs';
import { MMSCAN_PDF_VIEWER_CONFIG, MMSCAN_PDF_VIEWER_I18N } from './pdf-viewer-config';

export type MmscanPdfLinkTarget = 'none' | 'self' | 'blank' | 'parent' | 'top';

/** A zoom factor (1 = 100%), or `'page-width'` to fit the page to the viewer's width. */
export type MmscanPdfZoom = number | 'page-width';

export const MMSCAN_PDF_MIN_ZOOM = 0.5;
export const MMSCAN_PDF_MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;

/** Clamps a zoom factor to 0.5–3 and rounds it to two decimals. */
export function clampPdfZoom(value: number): number {
  const zoom = Number.isFinite(value) ? value : 1;
  return Math.round(Math.min(MMSCAN_PDF_MAX_ZOOM, Math.max(MMSCAN_PDF_MIN_ZOOM, zoom)) * 100) / 100;
}

type PdfJs = typeof import('pdfjs-dist');
type PdfJsViewer = typeof import('pdfjs-dist/web/pdf_viewer.mjs');

let pdfJsModules: Promise<{ lib: PdfJs; viewer: PdfJsViewer }> | undefined;

/**
 * Loads pdf.js once, on demand. The viewer bundle reads the API from `globalThis.pdfjsLib`,
 * which `pdfjs-dist` sets when it loads, so the imports run in order.
 */
function loadPdfJs(): Promise<{ lib: PdfJs; viewer: PdfJsViewer }> {
  pdfJsModules ??= import('pdfjs-dist')
    .then(async (lib) => ({ lib, viewer: await import('pdfjs-dist/web/pdf_viewer.mjs') }))
    .catch((error: unknown) => {
      pdfJsModules = undefined;
      throw error;
    });
  return pdfJsModules;
}

interface PdfJsInstance {
  lib: PdfJs;
  eventBus: EventBus;
  linkService: PDFLinkService;
  findController: PDFFindController;
  viewer: PDFViewer;
}

interface FindState {
  state?: number;
  matchesCount?: { current?: number; total?: number };
}

/** `FindState.PENDING` in pdf.js. */
const FIND_PENDING = 3;

const LINK_TARGETS: Record<MmscanPdfLinkTarget, 'NONE' | 'SELF' | 'BLANK' | 'PARENT' | 'TOP'> = {
  none: 'NONE',
  self: 'SELF',
  blank: 'BLANK',
  parent: 'PARENT',
  top: 'TOP',
};

const format = (template: string, values: Record<string, string | number>) =>
  template.replace(/\{(\w+)\}/g, (match, key: string) => (key in values ? String(values[key]) : match));

let nextId = 0;

/**
 * PDF viewer on Mozilla's pdf.js (loaded on demand) with search, zoom, page counter, print and
 * download. Needs `pdfjs-dist` in the app, `@import '@mmscan/angular-pdf-viewer/styles.css'` in its
 * stylesheet and the pdf.js worker served at `provideMmscanPdfViewer({ workerSrc })`
 * (default `/pdf.worker.min.mjs`).
 *
 * ```html
 * <mmscan-pdf-viewer [src]="url()" height="100%" [(search)]="query" print />
 * ```
 *
 * In the search field, Enter jumps to the next match, Shift+Enter to the previous one and Escape
 * clears the search. `showAll`, `renderText` and `externalLinksTarget` are read once, when the
 * viewer is created.
 */
@Component({
  selector: 'mmscan-pdf-viewer',
  imports: [MmscanAlert, MmscanButton, MmscanIcon, MmscanInput, MmscanSpinner],
  template: `
    @if (hasToolbar()) {
      <div
        role="toolbar"
        [attr.aria-label]="i18n().toolbar"
        [attr.aria-controls]="documentId"
        class="flex flex-wrap items-center gap-2"
      >
        @if (searchable()) {
          <div class="flex min-w-64 flex-1 items-center gap-2">
            <div class="relative min-w-32 flex-1">
              <mmscan-icon
                [icon]="icons.search"
                size="sm"
                class="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                mmscanInput
                type="search"
                class="ps-9 pe-9 [&::-webkit-search-cancel-button]:hidden"
                autocomplete="off"
                [attr.aria-label]="i18n().search"
                [attr.aria-describedby]="statusId"
                [placeholder]="searchPlaceholder() ?? i18n().search"
                [value]="search()"
                (input)="onSearchInput($event)"
                (keydown)="onSearchKeydown($event)"
              />
              @if (search()) {
                <button
                  type="button"
                  class="absolute end-1.5 top-1/2 inline-flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-2 focus-visible:outline-ring"
                  [attr.aria-label]="i18n().clearSearch"
                  (click)="clearSearch()"
                >
                  <mmscan-icon [icon]="icons.clear" size="sm" />
                </button>
              }
            </div>
            <span
              [id]="statusId"
              class="text-sm whitespace-nowrap text-muted-foreground tabular-nums"
              aria-live="polite"
              >{{ searchStatus() }}</span
            >
            @if (search()) {
              <button
                mmscanButton
                type="button"
                variant="ghost"
                severity="secondary"
                size="icon"
                [attr.aria-label]="i18n().previousMatch"
                [disabled]="matchCount() === 0"
                (click)="findPrevious()"
              >
                <mmscan-icon [icon]="icons.previous" size="sm" />
              </button>
              <button
                mmscanButton
                type="button"
                variant="ghost"
                severity="secondary"
                size="icon"
                [attr.aria-label]="i18n().nextMatch"
                [disabled]="matchCount() === 0"
                (click)="findNext()"
              >
                <mmscan-icon [icon]="icons.next" size="sm" />
              </button>
            }
          </div>
        }

        <div class="ms-auto flex items-center gap-1">
          <span class="text-sm whitespace-nowrap text-muted-foreground" aria-live="polite">{{ pageLabel() }}</span>

          @if (zoom()) {
            <div class="flex items-center gap-1">
              <button
                mmscanButton
                type="button"
                variant="ghost"
                severity="secondary"
                size="icon"
                [attr.aria-label]="i18n().zoomOut"
                [disabled]="scale() <= minZoom"
                (click)="zoomOut()"
              >
                <mmscan-icon [icon]="icons.zoomOut" size="sm" />
              </button>
              <button
                mmscanButton
                type="button"
                variant="ghost"
                severity="secondary"
                size="sm"
                class="min-w-16 tabular-nums"
                [attr.aria-label]="resetZoomLabel()"
                (click)="resetZoom()"
              >
                {{ zoomPercent() }}%
              </button>
              <button
                mmscanButton
                type="button"
                variant="ghost"
                severity="secondary"
                size="icon"
                [attr.aria-label]="i18n().zoomIn"
                [disabled]="scale() >= maxZoom"
                (click)="zoomIn()"
              >
                <mmscan-icon [icon]="icons.zoomIn" size="sm" />
              </button>
            </div>
          }

          @if (download()) {
            <a
              mmscanButton
              variant="ghost"
              severity="secondary"
              size="icon"
              target="_blank"
              download
              [href]="src()"
              [attr.aria-label]="i18n().download"
            >
              <mmscan-icon [icon]="icons.download" size="sm" />
            </a>
          }

          @if (print()) {
            <button
              mmscanButton
              type="button"
              variant="ghost"
              severity="secondary"
              size="icon"
              [attr.aria-label]="i18n().print"
              (click)="printPdf()"
            >
              <mmscan-icon [icon]="icons.print" size="sm" />
            </button>
          }
        </div>
      </div>
    }

    @if (printBlocked()) {
      <mmscan-alert severity="warning" dismissible (closed)="printBlocked.set(false)">{{
        i18n().printBlocked
      }}</mmscan-alert>
    }

    <div
      class="mmscan-pdf-viewer relative min-h-0 flex-auto overflow-hidden rounded-md border border-border bg-muted"
      [style.width]="width()"
      [style.height]="height()"
      [style.--mmscan-pdf-highlight-color]="textHighlightColor() ?? null"
      [style.--mmscan-pdf-highlight-selected-color]="textHighlightSelectedColor() ?? null"
    >
      <!-- pdf.js requires an absolutely positioned container around the .pdfViewer element. -->
      <div
        #container
        role="region"
        tabindex="0"
        [id]="documentId"
        [attr.aria-label]="i18n().document"
        [attr.aria-busy]="loading() || null"
        class="absolute inset-0 overflow-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
      >
        <div class="pdfViewer"></div>
      </div>
      @if (error()) {
        <div class="absolute inset-0 flex items-start justify-center p-4">
          <mmscan-alert severity="danger" class="w-full max-w-md">{{ i18n().loadError }}</mmscan-alert>
        </div>
      } @else if (loading()) {
        <div class="absolute inset-0 flex items-center justify-center">
          <mmscan-spinner />
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex h-full w-full min-h-0 flex-col gap-2' },
})
export class MmscanPdfViewer {
  protected readonly i18n = inject(MMSCAN_PDF_VIEWER_I18N);
  private readonly config = inject(MMSCAN_PDF_VIEWER_CONFIG);
  private readonly container = viewChild.required<ElementRef<HTMLDivElement>>('container');

  protected readonly icons = {
    search: LucideSearch,
    clear: LucideX,
    previous: LucideChevronUp,
    next: LucideChevronDown,
    zoomIn: LucidePlus,
    zoomOut: LucideMinus,
    download: LucideDownload,
    print: LucidePrinter,
  };
  protected readonly minZoom = MMSCAN_PDF_MIN_ZOOM;
  protected readonly maxZoom = MMSCAN_PDF_MAX_ZOOM;
  protected readonly documentId = `mmscan-pdf-viewer-${nextId++}`;
  protected readonly statusId = `${this.documentId}-status`;

  /** URL of the PDF. */
  readonly src = input.required<string>();
  /** Search query; two-way bindable. */
  readonly search = model('');
  /** Zoom factor, clamped to 0.5–3; two-way bindable. */
  /** Zoom factor, or `'page-width'` (default) to fit the width; zooming in or out switches to a factor. */
  readonly zoomLevel = model<MmscanPdfZoom>('page-width');
  readonly searchable = input(true, { transform: booleanAttribute });
  readonly zoom = input(true, { transform: booleanAttribute });
  readonly download = input(false, { transform: booleanAttribute });
  readonly print = input(false, { transform: booleanAttribute });
  /** Page counter in the toolbar. */
  readonly showPages = input(true, { transform: booleanAttribute });
  /** Scroll through all pages (`true`) or show one page at a time. */
  readonly showAll = input(true, { transform: booleanAttribute });
  /** Selectable, searchable text layer. */
  readonly renderText = input(true, { transform: booleanAttribute });
  readonly externalLinksTarget = input<MmscanPdfLinkTarget>('blank');
  /** Defaults to the translated "Search". */
  readonly searchPlaceholder = input<string>();
  /** CSS height of the document area. */
  readonly height = input('90vh');
  /** CSS width of the document area. */
  readonly width = input('100%');
  /** Search match background; keep it semi-transparent so the text stays visible. Defaults to the warning token. */
  readonly textHighlightColor = input<string>();
  /** Background of the current match. Defaults to the orange palette token. */
  readonly textHighlightSelectedColor = input<string>();

  /** Emits the page count once a document has loaded. */
  readonly loaded = output<number>();
  readonly loadError = output<unknown>();
  /** Emits the page number the user scrolled to. */
  readonly pageChange = output<number>();

  protected readonly loading = signal(true);
  protected readonly error = signal(false);
  protected readonly printBlocked = signal(false);
  protected readonly page = signal(1);
  protected readonly totalPages = signal(0);
  protected readonly matchCount = signal(0);
  protected readonly currentMatch = signal(0);
  protected readonly findPending = signal(false);

  protected readonly hasToolbar = computed(
    () => this.searchable() || this.zoom() || this.showPages() || this.download() || this.print()
  );
  /** Scale pdf.js actually renders at (resolved from `'page-width'`). */
  protected readonly scale = signal(1);
  protected readonly zoomPercent = computed(() => Math.round(this.scale() * 100));
  protected readonly resetZoomLabel = computed(() => format(this.i18n().resetZoom, { percent: this.zoomPercent() }));
  protected readonly pageLabel = computed(() =>
    this.showPages() && this.totalPages() > 0
      ? format(this.i18n().page, { page: this.page(), total: this.totalPages() })
      : ''
  );
  protected readonly searchStatus = computed(() => {
    if (!this.search() || this.findPending()) return '';
    return this.matchCount() > 0
      ? format(this.i18n().matchCount, { current: this.currentMatch(), total: this.matchCount() })
      : this.i18n().noMatches;
  });

  private readonly pdf = signal<PdfJsInstance | undefined>(undefined);
  private readonly abort = new AbortController();
  private pdfDocument?: PDFDocumentProxy;
  private loadingTask?: PDFDocumentLoadingTask;
  private currentSrc?: string;
  private lastQuery = '';
  private resizeTimer?: ReturnType<typeof setTimeout>;
  private destroyed = false;

  constructor() {
    afterNextRender(() => void this.initViewer());

    effect(() => {
      const pdf = this.pdf();
      const src = this.src();
      if (pdf && src && src !== this.currentSrc) untracked(() => void this.loadDocument(pdf, src));
    });

    effect(() => {
      const pdf = this.pdf();
      const zoom = this.zoomLevel();
      if (typeof zoom === 'number' && clampPdfZoom(zoom) !== zoom) {
        this.zoomLevel.set(clampPdfZoom(zoom));
        return;
      }
      if (pdf && this.pdfDocument) this.applyZoom(pdf.viewer, zoom);
    });

    effect(() => {
      const pdf = this.pdf();
      const query = this.search();
      if (!pdf || query === this.lastQuery) return;
      untracked(() => (query ? this.dispatchFind(query, '') : this.closeFind()));
    });

    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      clearTimeout(this.resizeTimer);
      this.abort.abort();
      void this.loadingTask?.destroy();
      void this.pdfDocument?.destroy();
    });
  }

  /**
   * pdf.js names page regions through its own l10n files, which aren't served; name them from
   * the package i18n so each page landmark is unique ("Page 1", "Page 2"…).
   */
  private labelPages(): void {
    const template = untracked(this.i18n).pageRegion;
    for (const page of this.container().nativeElement.querySelectorAll<HTMLElement>('.page[data-page-number]')) {
      page.setAttribute('aria-label', template.replace('{page}', page.dataset['pageNumber'] ?? ''));
    }
  }

  zoomIn(): void {
    this.zoomLevel.set(this.steppedZoom(1));
  }

  zoomOut(): void {
    this.zoomLevel.set(this.steppedZoom(-1));
  }

  /** Back to fitting the page width. */
  resetZoom(): void {
    this.zoomLevel.set('page-width');
  }

  /** Next 10% step from the current zoom, or from the rendered scale when fitting the width. */
  private steppedZoom(direction: 1 | -1): number {
    const zoom = this.zoomLevel();
    const steps = (typeof zoom === 'number' ? zoom : this.scale()) / ZOOM_STEP;
    const next = direction > 0 ? Math.floor(steps + 1e-6) + 1 : Math.ceil(steps - 1e-6) - 1;
    return clampPdfZoom(next * ZOOM_STEP);
  }

  private applyZoom(viewer: PDFViewer, zoom: MmscanPdfZoom): void {
    if (zoom === 'page-width') viewer.currentScaleValue = 'page-width';
    else viewer.currentScale = clampPdfZoom(zoom);
  }

  findNext(): void {
    this.findAgain(false);
  }

  findPrevious(): void {
    this.findAgain(true);
  }

  clearSearch(): void {
    this.search.set('');
    this.closeFind();
  }

  /** Opens the PDF in a new window and prints it; shows a message when pop-ups are blocked. */
  printPdf(): void {
    const printWindow = window.open(this.src(), '_blank');
    if (!printWindow) {
      this.printBlocked.set(true);
      return;
    }
    this.printBlocked.set(false);
    try {
      printWindow.addEventListener('load', () =>
        setTimeout(() => {
          try {
            printWindow.print();
          } catch {
            // Cross-origin window: the user prints from the opened tab.
          }
        }, 1000)
      );
    } catch {
      // Cross-origin window: the user prints from the opened tab.
    }
  }

  protected onSearchInput(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  protected onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.findAgain(event.shiftKey);
    } else if (event.key === 'Escape' && this.search()) {
      event.preventDefault();
      event.stopPropagation();
      this.clearSearch();
    }
  }

  private async initViewer(): Promise<void> {
    let modules: { lib: PdfJs; viewer: PdfJsViewer };
    try {
      modules = await loadPdfJs();
    } catch (error) {
      this.fail(error);
      return;
    }
    if (this.destroyed) return;

    const { lib, viewer: web } = modules;
    lib.GlobalWorkerOptions.workerSrc = this.config.workerSrc;

    const eventBus = new web.EventBus();
    const linkService = new web.PDFLinkService({
      eventBus,
      externalLinkTarget: web.LinkTarget[LINK_TARGETS[this.externalLinksTarget()]],
    });
    const findController = new web.PDFFindController({ eventBus, linkService });
    const Viewer = this.showAll() ? web.PDFViewer : web.PDFSinglePageViewer;
    // `abortSignal` (removes pdf.js' scroll and resize listeners) is missing from the shipped typings.
    const options = {
      container: this.container().nativeElement,
      eventBus,
      linkService,
      findController,
      removePageBorders: true,
      textLayerMode: this.renderText() ? 1 : 0,
      annotationEditorMode: lib.AnnotationEditorType.DISABLE,
      l10n: new web.GenericL10n('en'),
      abortSignal: this.abort.signal,
    };
    const viewer = new Viewer(options);
    linkService.setViewer(viewer);

    eventBus.on('pagesinit', () => {
      this.applyZoom(viewer, untracked(this.zoomLevel));
      this.labelPages();
    });
    eventBus.on('scalechanging', ({ scale }: { scale: number }) => this.scale.set(scale));
    eventBus.on('pagechanging', ({ pageNumber }: { pageNumber: number }) => {
      if (pageNumber === this.page()) return;
      this.page.set(pageNumber);
      this.pageChange.emit(pageNumber);
    });
    const onFind = ({ state, matchesCount }: FindState) => {
      if (state !== undefined) this.findPending.set(state === FIND_PENDING);
      if (matchesCount) {
        this.matchCount.set(matchesCount.total ?? 0);
        this.currentMatch.set(matchesCount.current ?? 0);
      }
    };
    eventBus.on('updatefindcontrolstate', onFind);
    eventBus.on('updatefindmatchescount', onFind);

    if (typeof ResizeObserver !== 'undefined') {
      // pdf.js only re-renders on scroll; redraw visible pages when the container resizes (e.g. a tab is shown).
      const observer = new ResizeObserver(() => {
        clearTimeout(this.resizeTimer);
        // Fit-width follows the new width; a fixed zoom only needs a redraw.
        this.resizeTimer = setTimeout(() => {
          if (!this.pdfDocument) return;
          if (untracked(this.zoomLevel) === 'page-width') viewer.currentScaleValue = 'page-width';
          else viewer.update();
        }, 100);
      });
      observer.observe(this.container().nativeElement);
      this.abort.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }

    this.pdf.set({ lib, eventBus, linkService, findController, viewer });
  }

  private async loadDocument(pdf: PdfJsInstance, src: string): Promise<void> {
    this.currentSrc = src;
    this.loading.set(true);
    this.error.set(false);
    this.totalPages.set(0);
    this.page.set(1);
    this.resetMatches();
    this.lastQuery = '';

    const previousTask = this.loadingTask;
    this.loadingTask = undefined;
    await previousTask?.destroy();
    if (this.pdfDocument) {
      pdf.viewer.setDocument(null as unknown as PDFDocumentProxy);
      pdf.linkService.setDocument(null, null);
      void this.pdfDocument.destroy();
      this.pdfDocument = undefined;
    }
    if (this.destroyed || this.currentSrc !== src) return;

    const task = pdf.lib.getDocument({
      url: src,
      cMapUrl: this.config.cMapUrl ?? `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdf.lib.version}/cmaps/`,
      cMapPacked: true,
      enableXfa: true,
      isEvalSupported: false, // CVE-2024-4367
    });
    this.loadingTask = task;
    try {
      const document = await task.promise;
      if (this.destroyed || this.currentSrc !== src) {
        void document.destroy();
        return;
      }
      this.pdfDocument = document;
      pdf.viewer.setDocument(document);
      pdf.linkService.setDocument(document, null);
      this.totalPages.set(document.numPages);
      this.loading.set(false);
      this.loaded.emit(document.numPages);
      const query = untracked(this.search);
      if (query) this.dispatchFind(query, '');
    } catch (error) {
      if (!this.destroyed && this.currentSrc === src) this.fail(error);
    } finally {
      if (this.loadingTask === task) this.loadingTask = undefined;
    }
  }

  private fail(error: unknown): void {
    console.error('Failed to load PDF document:', error);
    this.loading.set(false);
    this.error.set(true);
    this.loadError.emit(error);
  }

  private findAgain(previous: boolean): void {
    const query = this.search();
    if (query) this.dispatchFind(query, 'again', previous);
  }

  private dispatchFind(query: string, type: '' | 'again', findPrevious = false): void {
    const pdf = this.pdf();
    if (!pdf) return;
    this.lastQuery = query;
    this.findPending.set(true);
    pdf.eventBus.dispatch('find', {
      source: this,
      type,
      query,
      caseSensitive: false,
      entireWord: false,
      findPrevious,
      highlightAll: true,
      matchDiacritics: false,
      phraseSearch: true,
    });
  }

  /** Removes the highlights, like closing the pdf.js find bar. */
  private closeFind(): void {
    this.lastQuery = '';
    this.resetMatches();
    this.pdf()?.eventBus.dispatch('findbarclose', { source: this });
  }

  private resetMatches(): void {
    this.matchCount.set(0);
    this.currentMatch.set(0);
    this.findPending.set(false);
  }
}
